import { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useDialogs } from "@toolpad/core";
import type {
  AlertOptions,
  ConfirmOptions,
  DialogComponent,
  OpenDialogOptions,
  PromptOptions,
} from "@toolpad/core";
import { OpenDialogsContext } from "./OpenDialogsContext";

/**
 * A dialog dismissed by navigation was never answered, so the tracked promise
 * can resolve to the same result type as the dialog, or `undefined` if the user
 * navigated away before it answered.
 */
const notAnswered = undefined;

type TrackedOpenDialog = {
  <P extends undefined, R>(
    Component: DialogComponent<P, R>,
    payload?: P,
    options?: OpenDialogOptions<R>
  ): Promise<R | undefined>;
  <P, R>(
    Component: DialogComponent<P, R>,
    payload: P,
    options?: OpenDialogOptions<R>
  ): Promise<R | undefined>;
};

/**
 * Drop-in replacement for `useDialogs` that registers everything it opens with
 * {@link AppDialogsProvider}, so the dialog is dismissed if the user navigates
 * away. Without the provider in the tree it behaves exactly like `useDialogs`.
 */
export const useTrackedDialogs = () => {
  const dialogs = useDialogs();
  const track = useContext(OpenDialogsContext)?.track;

  return useMemo(() => {
    const dismissWith = <R>(
      handle: Promise<R>,
      result: R | undefined
    ): Promise<R | undefined> => {
      // Toolpad refuses undefined as a close result even for a navigation-
      // dismissed dialog, so we bridge the runtime undefined to the typed `R`.
      const closeResult = result as R;
      track?.(handle, () => {
        void dialogs.close(handle, closeResult);
      });
      return handle;
    };

    const open: TrackedOpenDialog = <P, R>(
      Component: DialogComponent<P, R>,
      payload?: P,
      options?: OpenDialogOptions<R>
    ) => {
      const handle =
        payload === undefined
          ? dialogs.open(
              Component as DialogComponent<undefined, R>,
              undefined,
              options
            )
          : dialogs.open(Component, payload, options);

      return dismissWith(handle, notAnswered);
    };

    return {
      ...dialogs,
      open,
      alert: (msg: ReactNode, options?: AlertOptions) =>
        dismissWith(dialogs.alert(msg, options), undefined),
      confirm: (msg: ReactNode, options?: ConfirmOptions) =>
        dismissWith(dialogs.confirm(msg, options), false),
      prompt: (msg: ReactNode, options?: PromptOptions) =>
        dismissWith(dialogs.prompt(msg, options), null),
    };
  }, [dialogs, track]);
};
